import { Link } from "react-router-dom";
import { CAT_MAP, formatDate } from "@/lib/categories";
import { ExternalLink, ArrowUpRight } from "lucide-react";

export function CategoryBadge({ slug }) {
  const c = CAT_MAP[slug];
  if (!c) return null;
  return (
    <span className={`inline-flex self-start w-fit items-center gap-1.5 px-2.5 py-1 text-[10px] font-meta font-semibold uppercase tracking-[0.12em] border rounded-full ${c.badge}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />{c.name}
    </span>
  );
}

export function SourceBadge({ name, date, className = "" }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-meta text-stone-500 ${className}`}>
      <span className="font-semibold text-stone-600">{name}</span>
      {date && <><span className="text-stone-300">·</span><span>{formatDate(date)}</span></>}
    </span>
  );
}

// Hero variant for the top featured article
export function HeroArticle({ article }) {
  const c = CAT_MAP[article.category];
  return (
    <Link to={`/articolo/${article.slug}`} data-testid="hero-featured-article"
      className={`group block rounded-2xl overflow-hidden bg-panel border border-line lift border-l-[6px] ${c?.accent}`}>
      <div className="overflow-hidden aspect-[16/9] bg-ink-deep">
        <img src={article.image_url} alt={article.title} draggable={false}
          className="w-full h-full object-cover group-hover:opacity-100 group-hover:scale-[1.03] transition-all duration-700" />
      </div>
      <div className="p-6 sm:p-8">
        <CategoryBadge slug={article.category} />
        <h2 className="font-editorial font-extrabold text-2xl sm:text-3xl lg:text-[38px] leading-[1.12] tracking-tight text-stone-900 mt-4 group-hover:text-gold transition-colors">
          {article.title}
        </h2>
        <p className="text-stone-600 mt-4 text-base leading-relaxed">{article.summary}</p>
        <div className="mt-6 flex items-center justify-between gap-3">
          <SourceBadge name={article.source_name} date={article.published_at} />
          <span className="text-gold opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all duration-300">
            <ArrowUpRight size={18} />
          </span>
        </div>
      </div>
    </Link>
  );
}

// Standard card
export function ArticleCard({ article, index = 0, compact = false }) {
  const c = CAT_MAP[article.category];
  return (
    <Link to={`/articolo/${article.slug}`} data-testid={`article-card-${index}`}
      className={`group flex flex-col rounded-2xl overflow-hidden bg-panel border border-line lift border-l-[6px] ${c?.accent}`}>
      {!compact && (
        <div className="overflow-hidden aspect-[16/10] bg-ink-deep">
          <img src={article.image_url} alt={article.title}
            className="w-full h-full object-cover opacity-100 group-hover:opacity-100 group-hover:scale-[1.04] transition-all duration-700" />
        </div>
      )}
      <div className="p-5 flex flex-col flex-1">
        <CategoryBadge slug={article.category} />
        <h3 className="font-editorial font-bold text-lg sm:text-xl leading-snug text-stone-900 mt-3 group-hover:text-gold transition-colors">
          {article.title}
        </h3>
        <p className="text-sm text-stone-600 mt-2 leading-relaxed line-clamp-3">{article.summary}</p>
        <div className="mt-4 pt-3 border-t border-line">
          <SourceBadge name={article.source_name} date={article.published_at} />
        </div>
      </div>
    </Link>
  );
}

// Text-only row for "latest" columns
export function ArticleRow({ article }) {
  return (
    <Link to={`/articolo/${article.slug}`} data-testid="latest-news-row"
      className="group block py-4 px-3 -mx-3 border-b border-line rounded-lg hover:bg-panel/70 transition-colors">
      <CategoryBadge slug={article.category} />
      <h4 className="font-editorial font-bold text-base sm:text-lg leading-snug text-stone-900 mt-2.5 group-hover:text-gold transition-colors">
        {article.title}
      </h4>
      <div className="mt-2">
        <SourceBadge name={article.source_name} date={article.published_at} />
      </div>
    </Link>
  );
}

// Compact row with a small thumbnail on the left ("Notizie di oggi")
export function ArticleRowThumb({ article }) {
  const c = CAT_MAP[article.category];
  return (
    <Link to={`/articolo/${article.slug}`} data-testid="today-news-row"
      className={`group flex gap-3 items-start py-3 px-3 -mx-3 border-b border-line rounded-xl hover:bg-panel/70 transition-colors`}>
      <span className={`shrink-0 w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-lg overflow-hidden bg-ink-deep border-l-[4px] ${c?.accent}`}>
        <img src={article.image_url} alt={article.title} loading="lazy" decoding="async"
          className="w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-500" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-meta font-semibold uppercase tracking-[0.1em] border rounded-full ${c?.badge}`}>
            {c?.short}
          </span>
          <span className="text-[10px] font-meta text-stone-400">{formatDate(article.published_at)}</span>
        </span>
        <span className="block font-editorial font-bold text-[15px] sm:text-base leading-snug text-stone-900 mt-1 line-clamp-3 group-hover:text-gold transition-colors">
          {article.title}
        </span>
      </span>
    </Link>
  );
}

export function ExternalSourceLink({ name, url }) {
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" data-testid="read-original-link"
      className="inline-flex items-center gap-2 text-sm font-semibold text-gold hover:text-gold-soft border-b border-gold/40 hover:border-gold pb-0.5 transition-colors">
      Leggi l'articolo originale su {name} <ExternalLink size={14} />
    </a>
  );
}
